import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { StorefrontSection } from '@mlm/shared';
import { useStorefrontContent } from '../../lib/hooks';

type Cfg = Extract<StorefrontSection, { type: 'packages' }>['config'];

const COLS: Record<NonNullable<Cfg['cols']>, string> = {
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

/** Membership tier cards — reads the `membershipTiers` CMS block. */
export function PackagesSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as Cfg;
  const tiers = useStorefrontContent().data.membershipTiers ?? [];
  if (tiers.length === 0) return null;
  const cols = cfg.cols ?? 4;

  return (
    <section className="bg-card py-14">
      <div className="mx-auto max-w-[1180px] px-4 text-center">
        <h2 className="font-heading text-[32px] font-extrabold text-foreground">{cfg.heading}</h2>
        {cfg.subheading && <p className="mt-2 text-sm text-muted-foreground">{cfg.subheading}</p>}
        <div className={`mt-10 grid gap-5 text-left ${COLS[cols]}`}>
          {tiers.map((t, i) => (
            <div
              key={i}
              className={`relative flex flex-col rounded-2xl border p-7 transition-transform hover:-translate-y-1.5 ${
                t.featured ? 'border-primary bg-accent shadow-elevated' : 'border-border bg-card'
              }`}
            >
              {t.featured && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white"
                  style={{ background: 'var(--brand-gradient)' }}
                >
                  Most Popular
                </span>
              )}
              <div className="text-[13px] font-bold uppercase tracking-wide text-primary">
                {t.name}
              </div>
              <div className="mt-2 font-heading text-[38px] font-extrabold leading-none text-foreground">
                {t.price}
              </div>
              {(t.pv || t.oneTimeLabel) && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {[t.pv, t.oneTimeLabel].filter(Boolean).join(' · ')}
                </div>
              )}
              <div className="my-4 h-px bg-border" />
              <ul className="flex-1 space-y-2">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-[13px] text-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={t.ctaHref}
                className={`mt-6 block rounded-[4px] py-2.5 text-center text-sm font-bold transition-colors ${
                  t.featured ? 'text-white' : 'border border-primary text-primary hover:bg-accent'
                }`}
                style={t.featured ? { background: 'var(--brand-gradient)' } : undefined}
              >
                {t.ctaLabel}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
